import { createSlice, PayloadAction } from "@reduxjs/toolkit";

/** 프론트에서 사용하는 게시글 표준 모델 */
export interface BoardItem {
    id: number;
    title: string;
    content: string;

    authorId: number | null;
    authorName?: string | null;

    createdAt: string;
    updatedAt?: string | null;

    commentCount?: number;
    viewCount?: number;

    imageUrl?: string | null;

    sector?: string | null;
    isPinned?: boolean;
    isTrending?: boolean;
    lastCommentAt?: string | null;

    isMine?: boolean;
    canDelete?: boolean;
    canReport?: boolean;
}

/** 상태 모델 */
export interface BoardState {
    pinned: BoardItem[];
    trending: BoardItem[];
    normal: BoardItem[];
    selectedBoard: BoardItem | null;
    loading: boolean;
    error: string | null;
}

const initialState: BoardState = {
    pinned: [],
    trending: [],
    normal: [],
    selectedBoard: null,
    loading: false,
    error: null,
};

/** 상대 이미지 경로를 같은 오리진 기준 URL로 정규화 */
function buildImageUrlFromRow(row: any): string | null {
    const rawImage =
        row.imageUrl ??
        row.image_url ??
        row.thumbnailUrl ??
        row.image_path ??
        row.filePath ??
        null;

    if (!rawImage || typeof rawImage !== "string") return null;

    if (/^https?:\/\//i.test(rawImage)) {
        return rawImage;
    }

    const cleaned = rawImage.replace(/^\/+/, "");
    return `/${cleaned}`;
}

/** 서버 row -> 표준 BoardItem */
function normalizeRow(row: any): BoardItem {
    const finalImageUrl = buildImageUrlFromRow(row);

    return {
        id: Number(row.id),
        title: row.title ?? "(제목 없음)",
        content: row.content ?? "",

        authorId: row.authorId ?? row.author_id ?? row.author?.id ?? null,
        authorName:
            row.authorName ??
            row.author_name ??
            row.author?.name ??
            row.writer ??
            null,

        createdAt: row.createdAt ?? row.created_at ?? row.regDate ?? "",
        updatedAt: row.updatedAt ?? row.updated_at ?? null,
        lastCommentAt: row.lastCommentAt ?? row.last_comment_at ?? null,

        commentCount: row.commentCount ?? row.comment_count ?? row.comments ?? 0,
        viewCount: row.viewCount ?? row.view_count ?? row.views ?? 0,

        imageUrl: finalImageUrl,
        sector: row.sector ?? null,

        isPinned: !!(row.isPinned ?? row.is_pinned),
        isTrending: !!(row.isTrending ?? row.is_trending),

        isMine: !!(row.isMine ?? row.is_mine),
        canDelete: !!(row.canDelete ?? row.can_delete),
        canReport: !!(row.canReport ?? row.can_report),
    };
}

/** 목록 분류 유틸 */
function splitBoardsByCategory(rows: any[]): {
    pinned: BoardItem[];
    trending: BoardItem[];
    normal: BoardItem[];
} {
    const pinned: BoardItem[] = [];
    const trending: BoardItem[] = [];
    const normal: BoardItem[] = [];

    for (const r of rows) {
        const item = normalizeRow(r);
        if (item.isPinned) pinned.push(item);
        else if (item.isTrending) trending.push(item);
        else normal.push(item);
    }

    return { pinned, trending, normal };
}

const boardSlice = createSlice({
    name: "board",
    initialState,
    reducers: {
        uploadBoardRequest(
            state,
            _action: PayloadAction<{ title: string; content: string; imageUrl?: any }>
        ) {
            state.loading = true;
            state.error = null;
        },
        uploadBoardSuccess(state) {
            state.loading = false;
        },
        uploadBoardFailure(state, action: PayloadAction<string>) {
            state.loading = false;
            state.error = action.payload;
        },

        fetchBoardListRequest(state) {
            state.loading = true;
            state.error = null;
        },
        fetchBoardListSuccess(state, action: PayloadAction<any>) {
            state.loading = false;

            if (
                action.payload &&
                typeof action.payload === "object" &&
                !Array.isArray(action.payload)
            ) {
                const { pinned = [], trending = [], normal = [] } = action.payload;
                state.pinned = Array.isArray(pinned) ? pinned.map(normalizeRow) : [];
                state.trending = Array.isArray(trending) ? trending.map(normalizeRow) : [];
                state.normal = Array.isArray(normal) ? normal.map(normalizeRow) : [];
                return;
            }

            const { pinned, trending, normal } = splitBoardsByCategory(
                Array.isArray(action.payload) ? action.payload : []
            );
            state.pinned = pinned;
            state.trending = trending;
            state.normal = normal;
        },
        fetchBoardListFailure(state, action: PayloadAction<string>) {
            state.loading = false;
            state.error = action.payload;
        },

        fetchBoardDetailRequest(state, _action: PayloadAction<number>) {
            state.loading = true;
            state.selectedBoard = null;
            state.error = null;
        },
        fetchBoardDetailSuccess(state, action: PayloadAction<any>) {
            state.loading = false;
            state.selectedBoard = normalizeRow(action.payload);
        },
        fetchBoardDetailFailure(state, action: PayloadAction<string>) {
            state.loading = false;
            state.error = action.payload;
        },

        resetBoardDetail(state) {
            state.selectedBoard = null;
            state.loading = false;
            state.error = null;
        },

        incrementViewCountRequest(_state, _action: PayloadAction<number>) {},
        incrementViewCountSuccess(state, action: PayloadAction<number>) {
            if (state.selectedBoard && state.selectedBoard.id === action.payload) {
                state.selectedBoard.viewCount = (state.selectedBoard.viewCount ?? 0) + 1;
            }
        },
        incrementViewCountFailure(state, action: PayloadAction<string>) {
            state.error = action.payload;
        },

        deleteBoardRequest(_state, _action: PayloadAction<number>) {},
        deleteBoardSuccess(state, action: PayloadAction<number>) {
            const delId = action.payload;
            state.pinned = state.pinned.filter((b) => b.id !== delId);
            state.trending = state.trending.filter((b) => b.id !== delId);
            state.normal = state.normal.filter((b) => b.id !== delId);
            if (state.selectedBoard?.id === delId) state.selectedBoard = null;
        },
        deleteBoardFailure(state, action: PayloadAction<string>) {
            state.error = action.payload;
        },

        reportBoardRequest(_state, _action: PayloadAction<{ id: number; reason: string }>) {},
        reportBoardSuccess(_state) {},
        reportBoardFailure(state, action: PayloadAction<string>) {
            state.error = action.payload;
        },
    },
});

export const {
    uploadBoardRequest,
    uploadBoardSuccess,
    uploadBoardFailure,

    fetchBoardListRequest,
    fetchBoardListSuccess,
    fetchBoardListFailure,

    fetchBoardDetailRequest,
    fetchBoardDetailSuccess,
    fetchBoardDetailFailure,

    resetBoardDetail,

    incrementViewCountRequest,
    incrementViewCountSuccess,
    incrementViewCountFailure,

    deleteBoardRequest,
    deleteBoardSuccess,
    deleteBoardFailure,

    reportBoardRequest,
    reportBoardSuccess,
    reportBoardFailure,
} = boardSlice.actions;

export default boardSlice.reducer;